import { getCurrentEnv } from '../utils/getCurrentEnv';
import { default as Modal } from 'antd/es/modal';
import { default as Tag } from 'antd/es/tag';
import { default as Collapse } from 'antd/es/collapse';
import { default as Skeleton } from 'antd/es/skeleton';
import { useState, useEffect, useMemo } from 'react';
import { PrismCode } from './PrismCode';

import { MetadataDisplay } from './CertificateMetadata';
import { extractCertificateMetadata } from '../utils/extractCertificateMetadata';

export function InpayPublicSSLCertificateLink() {
  const CURRENT_ENV = useMemo(() => getCurrentEnv(window.location.host), []);

  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      <span onClick={() => setIsModalOpen(true)} className="link">
        Inpay's Public SSL X.509 Certificate (Click to see)
      </span>
      <Modal
        title={
          <div className="flex items-center justify-center gap-4">
            <p>Inpay's Public SSL Certificate (X.509)</p>
            {CURRENT_ENV !== 'prod' && (
              <Tag bordered={false} className="bg-inpay-yellow-1000">
                <p className="text-bold dark:text-black">{CURRENT_ENV}</p>
              </Tag>
            )}
          </div>
        }
        open={isModalOpen}
        width={720}
        onOk={() => setIsModalOpen(false)}
        onCancel={() => setIsModalOpen(false)}
      >
        <CertificateModalContent />
      </Modal>
    </>
  );
}

function CertificateModalContent() {
  const CURRENT_ENV = useMemo(() => getCurrentEnv(window.location.host), []);

  const CERTIFICATE_PATH = useMemo(
    () =>
      `/docs/api/ssl-certificates/${CURRENT_ENV}-inpay-public-ssl-certificate.crt`,
    [CURRENT_ENV]
  );
  const CA_CHAIN_LINK = useMemo(
    () => `/docs/api/ca-chains/${CURRENT_ENV}-inpay-ca-chain.crt`,
    [CURRENT_ENV]
  );

  const [metadata, setMetadata] = useState<any>({});
  const [caMetadata, setCaMetadata] = useState<any>({});
  const [loading, setIsLoading] = useState(true);
  const [certificateContent, setCertificateContent] = useState('');
  const [caContent, setCaContent] = useState('');

  useEffect(() => {
    /**
     * fetches SSL and CA certificates and sets them in the state
     */
    async function fetchCertificates() {
      try {
        setIsLoading(true);
        const certificateResponse = await fetch(CERTIFICATE_PATH);
        const caResponse = await fetch(CA_CHAIN_LINK);

        const sslCertificateContent = await certificateResponse.text();
        const caContent = await caResponse.text();

        setCertificateContent(sslCertificateContent);
        setCaContent(caContent);

        setMetadata(extractCertificateMetadata(sslCertificateContent));
        setCaMetadata(extractCertificateMetadata(caContent));

        setIsLoading(false);
      } catch (error) {
        console.error('Failed to fetch certificate content:', error);
      }
    }

    fetchCertificates();
  }, [CA_CHAIN_LINK, CERTIFICATE_PATH]);

  if (loading) {
    return <Skeleton active />;
  }

  return (
    <div className="flex flex-col gap-4">
      <PrismCode showClipboard code={certificateContent} />
      <MetadataDisplay metadata={metadata} />
      <Collapse
        items={[
          {
            key: '1',
            label: 'CA Chain',
            children: (
              <div className="flex flex-col gap-4">
                <PrismCode showClipboard code={caContent} />
                <MetadataDisplay metadata={caMetadata} />
              </div>
            )
          }
        ]}
      />
      <a href={CERTIFICATE_PATH} className="link" download>
        Download Inpay Public SSL X.509 certificate
      </a>
      <a href={CA_CHAIN_LINK} className="link" download>
        Download CA chain
      </a>
    </div>
  );
}

export function CertificateContent() {
  const CURRENT_ENV = useMemo(() => getCurrentEnv(window.location.host), []);

  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <div className="flex items-center gap-4">
        <h2>Inpay's Public SSL Certificate (X.509)</h2>
        {CURRENT_ENV !== 'prod' && (
          <Tag bordered={false} className="bg-inpay-yellow-1000">
            <p className="text-bold dark:text-black">{CURRENT_ENV}</p>
          </Tag>
        )}
      </div>
      <CertificateModalContent />
    </div>
  );
}
